import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import moment from 'moment';
import { Solicitud } from '../../../../classes/Solicitud';
import { Breadcrumbs } from '../../../Home/Breadcrumb';
import Table, {
  ColumnDefinitionType,
} from '../../../../components/Table';
import Pagination from '../../../../components/Pagination';
import { Loading } from '../../../../components/Loading';
import { findSolicitudesByInstalacion } from '../../../../services/Service';
import { useAuth } from '../../../../hooks/useAuth';
import { useSolicitud } from '../../../../hooks/useSolicitud';
import { logger } from '../../../../utils/logger';

const PAGE_SIZE = 10;

// Pantalla PÚBLICA (sin login) a la que se accede al escanear el QR de
// una Instalación. Se clona el JSX de Solicitud/Table en lugar de
// reusar `TablaSolicitud` porque ese componente no recibe datos por
// props: lee `solicitudes` desde Redux/useSolicitud (búsqueda
// autenticada), lo que no aplica en una pantalla pública.
export const SolicitudesInstalacion = () => {
  const { id } = useParams();
  const { isAuth, handleRedirect } = useAuth();
  const { handleUrlVolver } = useSolicitud();

  const [solicitudes, setSolicitudes] = useState<Solicitud[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState<number>(1);

  useEffect(() => {
    if (!id) {
      return;
    }
    setLoading(true);
    findSolicitudesByInstalacion(id)
      .then((response) => {
        setSolicitudes(response.data || []);
        setCurrentPage(1);
      })
      .catch((error) => {
        logger.log(logger.safeError(error));
        setSolicitudes([]);
      })
      .finally(() => setLoading(false));
  }, [id]);

  const totalPages = Math.ceil(solicitudes.length / PAGE_SIZE);
  const solicitudesPagina = solicitudes.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const handleVerMas = (idSolicitud: number) => {
    const url = `/solicitud/${idSolicitud}?action=view`;
    if (!isAuth) {
      handleRedirect(url);
    }
    handleUrlVolver(`/instalacion/${id}/solicitudes`);
  };

  const columns: ColumnDefinitionType<Solicitud, keyof Solicitud>[] = [
    {
      key: 'numeroSolicitud',
      header: 'Número de Solicitud',
    },
    {
      key: 'instalacion',
      header: 'Instalación',
      cell: (data: Solicitud) => <>{data.instalacion ?? ''}</>,
    },
    {
      key: 'tipoTrabajo',
      header: 'Tipo de Trabajo',
    },
    {
      key: 'fechaInicio',
      header: 'Fecha Inicio',
      cell: (data: Solicitud) => (
        <>
          {data.fechaInicio
            ? moment(data.fechaInicio).format('DD/MM/YYYY')
            : ''}
        </>
      ),
    },
    {
      key: 'fechaFin',
      header: 'Fecha Fin',
      cell: (data: Solicitud) => (
        <>
          {data.fechaFin
            ? moment(data.fechaFin).format('DD/MM/YYYY')
            : ''}
        </>
      ),
    },
    {
      key: 'estado',
      header: 'Estado',
    },
    {
      key: 'empresaGestora',
      header: 'Empresa Gestora',
    },
    {
      key: 'contratista',
      header: 'Contratista',
    },
    {
      key: 'id',
      header: 'Acciones',
      cell: (data: Solicitud) => (
        <Link
          to={`/solicitud/${data.id}?action=view`}
          title="Ver más"
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => {
            e.stopPropagation();
            handleVerMas(data.id);
          }}
        >
          <i
            className="fa fa-eye text-secondary me-2"
            aria-hidden="true"
          ></i>
        </Link>
      ),
    },
  ];

  return (
    <>
      <div className="content-page" style={{ marginLeft: '0px' }}>
        <div className="content">
          <div className="container-fluid">
            <Breadcrumbs title={'Solicitudes de la Instalación'} />
            <div className="table-responsive">
              {loading ? (
                <Loading />
              ) : (
                <>
                  <Table
                    data={solicitudesPagina}
                    columns={columns}
                    notResults="No existen solicitudes para esta instalación"
                  ></Table>
                  <div className="mt-3">
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={setCurrentPage}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SolicitudesInstalacion;
